import React, { Fragment, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import icon from '../../images/icon/magic.svg';
import Seo from '../../components/Seo';

const FAQPage = () => {
    const { t, i18n } = useTranslation();
    const { lang } = useParams();
    const [openQuestion, setOpenQuestion] = useState(null);

    useEffect(() => {
        if (lang && (lang === 'en' || lang === 'ka')) {
            i18n.changeLanguage(lang);
        }
    }, [lang, i18n]);

    const questions = ['q1', 'q2', 'q3', 'q4', 'q5', 'q6'];

    const toggleQuestion = (qKey) => {
        setOpenQuestion(openQuestion === qKey ? null : qKey);
    };

    return (
        <Fragment>
            <Seo
                title="FAQ | OrbitroniX"
                description="Find answers to frequently asked questions about OrbitroniX services, projects, and support."
                keywords="FAQ, frequently asked questions, OrbitroniX help"
                url={`/${lang}/faq`}
            />
            <Layout>
                <motion.section
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="page-title pt-150 pos-rel bg_img"
                >
                    <div className="container">
                        <div className="page-title-wrap sd-title-wrap">
                            <div className="row mt-none-30 align-items-end">
                                <div className="col-lg-9 mt-30">
                                    <motion.div
                                        initial={{ opacity: 0, x: -50 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.3 }}
                                        className="page-title-box"
                                    >
                                        <motion.span
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ delay: 0.5 }}
                                            className="sub-title"
                                        >
                                            <img src={icon} alt="" /> FAQ
                                        </motion.span>
                                        <motion.h2
                                            initial={{ opacity: 0, y: 30 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.7 }}
                                            className="title"
                                        >
                                            {t('faq.title')}
                                        </motion.h2>
                                    </motion.div>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.section>

                <motion.section
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5, duration: 0.8 }}
                    className="terms&conditions-section pb-85 cp-det-bg"
                >
                    <div className="container">
                        <div className="row justify-content-center">
                            <div className="col-lg-8">
                                <p className="text-white-50 mb-5 text-center" style={{ fontSize: '18px' }}>
                                    {t('faq.subtitle')}
                                </p>

                                <div className="faq-list">
                                    {questions.map((qKey, index) => (
                                        <motion.div
                                            key={qKey}
                                            className="faq-item mb-3"
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.7 + index * 0.1 }}
                                        >
                                            <button
                                                className={`faq-question ${openQuestion === qKey ? 'active' : ''}`}
                                                onClick={() => toggleQuestion(qKey)}
                                            >
                                                <span>{t(`faq.questions.${qKey}.question`)}</span>
                                                <i className={`fas fa-chevron-${openQuestion === qKey ? 'up' : 'down'}`}></i>
                                            </button>
                                            <AnimatePresence>
                                                {openQuestion === qKey && (
                                                    <motion.div
                                                        className="faq-answer"
                                                        initial={{ height: 0, opacity: 0 }}
                                                        animate={{ height: 'auto', opacity: 1 }}
                                                        exit={{ height: 0, opacity: 0 }}
                                                        transition={{ duration: 0.3 }}
                                                    >
                                                        <p>{t(`faq.questions.${qKey}.answer`)}</p>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </motion.div>
                                    ))}
                                </div>

                                <div className="text-center mt-5">
                                    <p className="text-white-50 mb-3">Still have questions?</p>
                                    <Link to={`/${lang}/contact`} className="orbx-btn">
                                        Contact Us <i className="fas fa-arrow-right"></i>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.section>

                <style>{`
                    .faq-question {
                        width: 100%;
                        background: rgb(8 11 37);
                        border: 1px solid rgba(255, 255, 255, 0.08);
                        border-radius: 12px;
                        padding: 20px 24px;
                        color: #fff;
                        font-size: 16px;
                        font-weight: 600;
                        text-align: left;
                        cursor: pointer;
                        transition: all 0.3s ease;
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                    }
                    .faq-question:hover {
                        background: rgba(108, 92, 231, 0.1);
                        border-color: rgba(108, 92, 231, 0.3);
                    }
                    .faq-question.active {
                        background: rgba(108, 92, 231, 0.15);
                        border-color: rgba(108, 92, 231, 0.4);
                        border-radius: 12px 12px 0 0;
                    }
                    .faq-question i {
                        color: #6c5ce7;
                        font-size: 14px;
                        transition: transform 0.3s ease;
                    }
                    .faq-answer {
                        background: rgba(255, 255, 255, 0.02);
                        border: 1px solid rgba(108, 92, 231, 0.2);
                        border-top: none;
                        border-radius: 0 0 12px 12px;
                        padding: 0 24px;
                        overflow: hidden;
                    }
                    .faq-answer p {
                        color: rgba(255, 255, 255, 0.7);
                        font-size: 15px;
                        line-height: 1.8;
                        margin: 0;
                        padding: 20px 0;
                    }
                `}</style>
            </Layout>
        </Fragment>
    );
};

export default FAQPage;
